<template>
    <section ref="partnership" class="partnership">
        <!-- LEI LOGO -->
        <div v-if="showLeiLogo" class="logo-wr logo-wr--lei">
            <img
                class="logo-item logo-item--lei"
                src="/images/LEILogo.png"
                alt="LEI Logo"
            />
        </div>

        <!-- GLEIF LOGO -->
        <div v-if="showGleifLogo" class="logo-wr">
            <img
                class="logo-item logo-item--gleif"
                src="/images/logo/GLEIF.png"
                alt="GLEIF Logo"
            />
        </div>

        <!-- HEADER -->
        <div v-if="showHeader" class="partnership__header">
            <div v-if="$slots['partner-logo']" class="partnership__logo">
                <slot name="partner-logo" />
            </div>
            <h1 class="partnership__title">
                {{ header.title }}
            </h1>
            <div class="partnership__desc-wrapper">
                <p
                    v-for="(desc, index) in header.descriptions"
                    :key="index"
                    class="partnership__desc"
                    v-html="desc"
                />
            </div>

            <Steps :steps="steps" />
        </div>

        <!-- MAIN -->
        <div v-if="wrapped" class="partnership__wrapper">
            <div v-if="wrapperTitle" class="partnership__wrapper-title">
                {{ wrapperTitle }}
            </div>
            <Tabs
                :tabs="tabs"
                :active-tab="activeTabIndex"
                @change-tab="setActiveTabContent"
            >
                <template v-if="showForm" #content>
                    <keep-alive>
                        <component
                            :is="activeComponentName"
                            :company="company"
                            :api-endpoints="apiEndpoints"
                            :contact-email="contactEmail"
                            :all-packages="allPackages"
                            :jurisdictions="translatedJurisdictions"
                            :country-code="countryCode"
                            :initial-currency="initialCurrency.toUpperCase()"
                            :custom-legal-entity-label="customLegalEntityLabel"
                            :custom-legal-entity-en-label="
                                customLegalEntityEnLabel
                            "
                            :custom-company-number-label="
                                customCompanyNumberLabel
                            "
                            :upload-description="uploadDescription"
                            :show-new-lei-about="showNewLeiAbout"
                            @hook:activated="sendHeightToParent"
                            @hook:updated="sendHeightToParent"
                        ></component>
                    </keep-alive>
                </template>
            </Tabs>
            <div class="partnership__copyright">
                {{ $t("general.poweredByText") }}
            </div>
        </div>
        <template v-else>
            <Tabs
                :tabs="tabs"
                :active-tab="activeTabIndex"
                @change-tab="setActiveTabContent"
            >
                <template v-if="showForm" #content>
                    <keep-alive>
                        <component
                            :is="activeComponentName"
                            :company="company"
                            :api-endpoints="apiEndpoints"
                            :contact-email="contactEmail"
                            :all-packages="allPackages"
                            :jurisdictions="translatedJurisdictions"
                            :country-code="countryCode"
                            :initial-currency="initialCurrency.toUpperCase()"
                            :custom-legal-entity-label="customLegalEntityLabel"
                            :custom-legal-entity-en-label="
                                customLegalEntityEnLabel
                            "
                            :custom-company-number-label="
                                customCompanyNumberLabel
                            "
                            :upload-description="uploadDescription"
                            :show-new-lei-about="showNewLeiAbout"
                            @hook:activated="sendHeightToParent"
                            @hook:updated="sendHeightToParent"
                        ></component>
                    </keep-alive>
                </template>
            </Tabs>
            <div class="partnership__copyright">
                {{ customCopyright || $t("general.poweredByText") }}
            </div>
        </template>
        <template v-if="activeAboutComponentName">
            <div :class="{ partnership__wrapper: wrapped }">
                <component
                    :is="activeAboutComponentName"
                    :initial-currency="initialCurrency.toUpperCase()"
                    :all-packages="allPackages"
                />
            </div>
        </template>
    </section>
</template>

<script>
const DEFAULT_LOCALE = "en";

export default {
    components: {
        NewLEIAbout: () => import("@comp/iframes/partners/NewLEIAbout.vue"),
        NewLEIForm: () => import("@comp/iframes/partners/NewLEIForm.vue"),
        RenewLEIForm: () => import("@comp/iframes/partners/RenewLEIForm.vue"),
        Tabs: () => import("@comp/iframes/_shared/Tabs.vue"),
        Steps: () => import("@comp/iframes/_shared/Steps.vue"),
    },
    props: {
        company: {
            type: Object,
            required: true,
        },
        apiEndpoints: {
            type: Object,
            required: true,
        },
        contactEmail: {
            type: String,
            required: true,
        },
        packages: {
            type: Object,
            required: true,
        },
        renewPackages: {
            type: [Object, null],
            required: true,
            default: null,
        },
        jurisdictions: {
            type: Object,
            required: true,
        },
        initialCurrency: {
            type: String,
            default: "EUR",
        },
        targetAppUrl: {
            type: String,
            required: true,
        },
        showHeader: {
            type: Boolean,
            default: false,
        },
        partnerLogo: {
            type: String,
            default: "",
        },
        header: {
            type: Object,
            default: () => {
                return {
                    title: "LEI Worldwide - Global LEI Registration/Renewal",
                    descriptions: [
                        "Register or renew any LEI Code for any company or legal entity here.",
                        "To begin, simply enter your contact details and then the details of your legal entity that is applying for an LEI.",
                    ],
                };
            },
        },
        showGleifLogo: {
            type: Boolean,
            default: false,
        },
        customCopyright: {
            type: String,
        },
        locale: {
            type: String,
            default: "en",
        },
        countryCode: {
            type: String,
            default: "",
        },
        showLeiLogo: {
            type: Boolean,
            default: false,
        },
        wrapped: {
            type: Boolean,
            default: false,
        },
        wrapperTitle: {
            type: String,
            default: "",
        },
        steps: {
            type: Array,
            default: () => [
                "Enter contact details and legal entity details",
                "Select your package and proceed to checkout",
                "Enter final details after checkout, and wait for your LEI to be issued via email",
            ],
        },
        customNewLeiTabLable: {
            type: String,
            default: "",
        },
        customRenewLeiTabLable: {
            type: String,
            default: "",
        },
        customLegalEntityLabel: {
            type: String,
            default: "",
        },
        customLegalEntityEnLabel: {
            type: String,
            default: "",
        },
        customCompanyNumberLabel: {
            type: String,
            default: "",
        },
        uploadDescription: {
            type: String,
            default: "",
        },
        showNewLeiAbout: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            activeTabIndex: 0,
            showForm: false,
            countriesByLocale: null,
            resizeObserver: null,
            heightFrame: null,
            lastSentHeight: 0,
        };
    },
    computed: {
        tabs() {
            return [
                {
                    header:
                        this.customNewLeiTabLable || this.$t("tabs.applyNew"),
                    componentName: "NewLEIForm",
                    componentNameAbout: this.showNewLeiAbout && "NewLEIAbout",
                    hash: "newlei",
                },
                {
                    header:
                        this.customRenewLeiTabLable || this.$t("tabs.renew"),
                    componentName: "RenewLEIForm",
                    hash: "renew",
                },
            ];
        },
        activeComponentName() {
            return this.tabs[this.activeTabIndex].componentName;
        },
        activeAboutComponentName() {
            return this.tabs[this.activeTabIndex].componentNameAbout;
        },
        allPackages() {
            const isRenew = this.activeTabIndex === 1;
            if (isRenew && this.renewPackages) return this.renewPackages;
            return this.packages;
        },
        translatedJurisdictions() {
            if (this.locale === DEFAULT_LOCALE || !this.countriesByLocale)
                return this.jurisdictions;
            const countries = this.jurisdictions.countries || {};
            const translatedCountries = Object.keys(countries).reduce(
                (acc, key) => {
                    if (this.countriesByLocale[key]) {
                        acc[key] = this.countriesByLocale[key];
                    }
                    return acc;
                },
                {},
            );
            return {
                ...this.jurisdictions,
                countries: {
                    ...this.jurisdictions.countries,
                    ...translatedCountries,
                },
            };
        },
    },
    watch: {
        locale: {
            immediate: true,
            async handler(val) {
                if (val !== DEFAULT_LOCALE) {
                    await Promise.all([
                        this.loadAndSetLocale(val),
                        this.loadCountries(val),
                    ]);
                }
            },
        },
    },
    mounted() {
        const hash = window.location.hash.slice(1);
        const activeTabIndex = this.tabs.findIndex((t) => t.hash === hash);
        if (hash && activeTabIndex >= 0) {
            this.activeTabIndex = activeTabIndex;
        }

        this.appURL = window.location.origin;
        // section is not gated behind showForm, so report from here
        this.setIFrameHeight();

        // catch Google autocomplete
        setTimeout(() => {
            this.showForm = true;
        }, 500);
    },
    beforeDestroy() {
        this.stopReportingHeight();
    },
    methods: {
        setIFrameHeight() {
            const el = this.$refs.partnership;
            if (!el) {
                return;
            }

            // Content keeps growing after first paint (chunks, logos, fonts,
            // validation errors); one measurement leaves the host too short.
            if (typeof ResizeObserver !== "undefined") {
                this.resizeObserver = new ResizeObserver(
                    this.sendHeightToParent,
                );
                this.resizeObserver.observe(el);
                return;
            }

            window.addEventListener("resize", this.sendHeightToParent);
            this.sendHeightToParent();
        },
        stopReportingHeight() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
                this.resizeObserver = null;
            }
            window.removeEventListener("resize", this.sendHeightToParent);
            if (this.heightFrame) {
                window.cancelAnimationFrame(this.heightFrame);
                this.heightFrame = null;
            }
        },
        sendHeightToParent() {
            // one message per frame, none when the height has not moved
            if (this.heightFrame) {
                return;
            }
            this.heightFrame = window.requestAnimationFrame(() => {
                this.heightFrame = null;
                const el = this.$refs.partnership;
                if (!el) {
                    return;
                }
                const height = el.offsetHeight + 20;
                if (height === this.lastSentHeight) {
                    return;
                }
                this.lastSentHeight = height;
                window.parent.postMessage({ height }, this.targetAppUrl);
            });
        },
        setActiveTabContent(tabIndex) {
            this.activeTabIndex = tabIndex;
        },
        async loadAndSetLocale(locale) {
            try {
                const messages = await import(`@/locales/${locale}.json`);
                this.$i18n.setLocaleMessage(locale, messages.default);
                this.$i18n.locale = locale;
            } catch (error) {
                console.error(`Error loading locale "${locale}":`, error);
            }
        },
        async loadCountries(locale) {
            try {
                const module = await import(
                    `i18n-iso-countries/langs/${locale}.json`
                );
                this.countriesByLocale = module.default?.countries || null;
            } catch (error) {
                console.error(`Failed to load countries for ${locale}:`, error);
            }
        },
    },
};
</script>

<style lang="scss">
@use "@sass/abstracts" as *;

body {
    margin: 0;
    background-color: var(--bg-color);

    * {
        box-sizing: border-box;
    }

    a {
        color: var(--accent-color);
    }

    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active {
        -webkit-background-clip: text;
        -webkit-text-fill-color: var(--primary-text-color);
        transition: background-color 5000s ease-in-out 0s;
        box-shadow: inset 0 0 0 0 var(--bg-color);
    }
}

.partnership {
    font-family: "Open Sans", sans-serif;
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 3rem 1rem 2rem;
    box-sizing: border-box;

    &__wrapper {
        margin-top: 60px;
        padding: 64px 70px;
        border-radius: 20px;
        border: 1px solid var(--wrapper-color-bg);
        background-color: var(--wrapper-color-bg);

        --bg-color: var(--wrapper-color-bg);

        @include mobile {
            padding: 2rem;
        }

        &-title {
            margin-bottom: 60px;
            text-align: center;
            font-family: var(--font-main);
            font-size: 50px;
            font-weight: 700;
            line-height: 130%;
            color: var(--primary-text-color);

            @include mobile {
                margin-bottom: 36px;
                font-size: 30px;
            }
        }
    }

    &__header {
        display: flex;
        flex-direction: column;
        gap: 32px;
        margin-bottom: 48px;
        align-items: center;
        @include mobile {
            margin-bottom: 1.5rem;
        }
    }

    &__title {
        @include font(
            $size: var(--title-size),
            $color: var(--primary-text-color),
            $weight: 700
        );
        text-align: center;
        margin: 0;
    }

    &__desc {
        @include font(
            $size: var(--desc-size),
            $color: var(--light-text-color),
            $weight: 400
        );
        text-align: center;
        margin: 0 0 1.2rem;
    }

    &__copyright {
        text-align: center;
        color: var(--primary-text-color);
    }
}

.logo {
    &-wr {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        margin-bottom: 1rem;

        &--lei {
            width: max-content;
            margin: 0 auto 60px;
            padding: 6px 14px;
            justify-content: center;
            border-radius: 90px;
            background: rgba(255, 255, 255, 0.06);
        }
    }
    &-item {
        &--lei {
            width: 202px;
        }
        &--gleif {
            width: 100px;
        }
    }
}
</style>
